import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { config } from '@/config'
import { STORAGE_KEYS } from '@/shared/constants'

/**
 * Create axios instance with base configuration
 */
const createHttpClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.apiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: false,
  })

  return client
}

export const httpClient = createHttpClient()

export const clearAuthStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT)
  localStorage.removeItem(STORAGE_KEYS.USER)
  localStorage.removeItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
}

const storeTokens = (
  accessToken: string,
  accessTokenExpiresAt: number,
  refreshToken?: string,
  refreshTokenExpiresAt?: number
) => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken)
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT, String(accessTokenExpiresAt))

  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
  }

  if (refreshTokenExpiresAt) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT, String(refreshTokenExpiresAt))
  }
}

const getExpiration = (key: string): number | null => {
  const value = localStorage.getItem(key)
  if (!value) return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

export const isAuthTokenValid = (): boolean => {
  const expiresAt = getExpiration(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT)
  if (!expiresAt) return false
  return expiresAt > Date.now()
}

export const isRefreshTokenValid = (): boolean => {
  const expiresAt = getExpiration(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT)
  if (!expiresAt) return false
  return expiresAt > Date.now()
}

export const setAuthTokens = (
  accessToken: string,
  expiresIn: number,
  refreshToken?: string,
  refreshExpiresIn?: number
) => {
  const accessTokenExpiresAt = Date.now() + expiresIn * 1000
  const refreshTokenExpiresAt = refreshExpiresIn ? Date.now() + refreshExpiresIn * 1000 : undefined
  storeTokens(accessToken, accessTokenExpiresAt, refreshToken, refreshTokenExpiresAt)
}

export const isTokenValid = (token?: string): boolean => {
  if (!token) return false

  const parts = token.split('.')
  if (parts.length !== 3) {
    return false
  }

  try {
    const payloadPart = parts[1]
    if (!payloadPart) return false
    const payloadJson = atob(payloadPart)
    const payload = JSON.parse(payloadJson)

    if (!payload.exp) {
      return false
    }

    const expiry = Number(payload.exp) * 1000
    return !Number.isNaN(expiry) && expiry > Date.now()
  } catch {
    return false
  }
}

/**
 * Request interceptor
 */
httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token) {
    const headers = (config.headers || {}) as any
    headers.Authorization = `Bearer ${token}`
    config.headers = headers
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Response interceptor
 */
httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    const isLoginRequest = originalRequest?.url?.includes('/auth/login')

    // Don't redirect on login 401 - let LoginPage handle it (e.g. redirect to verify-otp for "Account disabled")
    if (isLoginRequest && error.response?.status === 401) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true

      const refreshValid = isRefreshTokenValid()

      if (refreshValid) {
        // No /auth/refresh endpoint available, keep user active during refresh window.
        // Future requests may still fail until re-login but we do not log them out immediately.
        return Promise.reject(error)
      }

      clearAuthStorage()
      window.location.href = '/'
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      const refreshValid = isRefreshTokenValid()
      if (!refreshValid) {
        clearAuthStorage()
        window.location.href = '/'
      }
    }

    return Promise.reject(error)
  }
)

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

/**
 * API error response
 */
export interface ApiError {
  message: string
  errors?: Record<string, string[]>
}

